import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Typography, Card, CardContent, Container } from '@mui/material';

const LandingPage = ({ onSelectType }) => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 3,
      }}
    >
      <Container maxWidth="md">
        <Card
          sx={{
            borderRadius: 4,
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {/* Hero Section */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                color: 'white',
                p: 6,
                textAlign: 'center',
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  mb: 2,
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  background: 'linear-gradient(45deg, #ffffff 0%, #f0f0f0 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Dhandhapaani
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  fontWeight: 400,
                  opacity: 0.9,
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                }}
              >
                Dhandhapaani - Complete Business Management Solution
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mb: 2,
                  opacity: 0.85,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                Powered by Maruti Agritech
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  opacity: 0.8,
                  maxWidth: 600,
                  mx: 'auto',
                  lineHeight: 1.6,
                }}
              >
                Streamline your operations, manage inventory, track sales, and grow your business with our comprehensive platform.
              </Typography>
            </Box>

            {/* Role Selection Section */}
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography
                variant="h4"
                sx={{
                  mb: 3,
                  fontWeight: 700,
                  color: 'primary.main',
                  fontSize: { xs: '1.75rem', md: '2.125rem' },
                }}
              >
                Choose Your Role
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 5,
                  color: 'text.secondary',
                  maxWidth: 500,
                  mx: 'auto',
                  lineHeight: 1.6,
                }}
              >
                Select how you'd like to use our platform and get started with the right tools for your needs.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 4,
                  maxWidth: 800,
                  mx: 'auto',
                }}
              >
                {/* Business Owner Card */}
                <Box sx={{ flex: 1 }}>
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      border: '2px solid transparent',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(99, 102, 241, 0.2)',
                      },
                    }}
                    onClick={() => onSelectType('business-owner')}
                  >
                    <CardContent sx={{ p: 4, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ fontSize: '4rem', mb: 3 }}>🏢</Box>
                      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: 'primary.main' }}>
                        Business Owner
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', flex: 1 }}>
                        Manage your business operations, track inventory, monitor sales, handle customers, and access powerful business intelligence tools.
                      </Typography>
                      <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        sx={{
                          mt: 'auto',
                          py: 1.5,
                          fontSize: '1rem',
                          fontWeight: 600,
                          background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #5855eb 0%, #9333ea 100%)',
                          },
                        }}
                      >
                        Get Started
                      </Button>
                    </CardContent>
                  </Card>
                </Box>

                {/* Customer Card */}
                <Box sx={{ flex: 1 }}>
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      border: '2px solid transparent',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: 'success.main',
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(16, 185, 129, 0.2)',
                      },
                    }}
                    onClick={() => onSelectType('customer')}
                  >
                    <CardContent sx={{ p: 4, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ fontSize: '4rem', mb: 3 }}>🛍️</Box>
                      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: 'success.main' }}>
                        Customer
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', flex: 1 }}>
                        Access invoices, view order history, and follow payment status in a simple customer portal connected to your business records.
                      </Typography>
                      <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        sx={{
                          mt: 'auto',
                          py: 1.5,
                          fontSize: '1rem',
                          fontWeight: 600,
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                          },
                        }}
                      >
                        Open Portal
                      </Button>
                    </CardContent>
                  </Card>
                </Box>
              </Box>

              <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid #e5e7eb' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  By using Dhandhapaani, you agree to our{' '}
                  <Typography
                    component={RouterLink}
                    to="/privacy"
                    sx={{
                      color: 'primary.main',
                      textDecoration: 'none',
                      fontWeight: 600,
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    Privacy Policy
                  </Typography>{' '}
                  and{' '}
                  <Typography
                    component={RouterLink}
                    to="/terms"
                    sx={{
                      color: 'primary.main',
                      textDecoration: 'none',
                      fontWeight: 600,
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    Terms of Service
                  </Typography>
                  .
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default LandingPage;



